import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Sparkles, ArrowRight, Mail, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, requestMagicLink } = useAuth();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [previewLink, setPreviewLink] = useState(null);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const result = await requestMagicLink(email);
      // Handle the "no access" case (members/guests/unknown role)
      if (result?.ok === false && result?.code === "no_access") {
        toast.error(result.message || t("login.no_access", "Aucun accès à DELPHI pour ce compte. Merci de contacter votre Consultant."));
        return;
      }
      setSent(true);
      if (result?.preview_link) {
        setPreviewLink(result.preview_link);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login.error_generic", "Une erreur est survenue. Réessayez."));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSent(false);
    setPreviewLink(null);
    setEmail("");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Top-right language switcher (visible before login) */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-4 mb-12" data-testid="login-brand">
            <div className="w-12 h-12 rounded-full bg-apollo-orange flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col items-center leading-none">
              <img src="/apollo-logo.png" alt="APOLLO" className="h-9 w-auto object-contain" data-testid="login-apollo-logo" />
              <div className="font-heading font-black text-3xl tracking-tighter text-apollo-orange -mt-1.5">DELPHI</div>
            </div>
          </div>

          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-text-primary mb-3">
            {t("login.title", "L'oracle qui sait.")}
          </h1>
          <p className="font-oracle italic text-xl text-apollo-orange-dark leading-relaxed mb-8" data-testid="login-tagline">
            « {t("login.tagline", "Diagnostiquez vos groupes. Prescrivez les bons ateliers.")} »
          </p>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="text-sm font-medium text-text-primary mb-1.5 block">
                  {t("login.email_label", "Email APOLLO")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                  <Input
                    type="email"
                    required
                    placeholder="vous@apollo-business.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 text-base"
                    data-testid="login-email-input"
                  />
                </div>
                <p className="text-xs text-text-secondary mt-2">
                  {t("login.email_hint", "Un lien de connexion à usage unique vous sera envoyé par email.")}
                </p>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="w-full h-12 bg-apollo-orange hover:bg-apollo-orange-dark text-white font-semibold text-base rounded-md group"
              >
                {submitting
                  ? t("login.sending", "Envoi du lien…")
                  : t("login.send_btn", "Recevoir mon lien de connexion")}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          ) : (
            <div className="space-y-5" data-testid="login-sent">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-900">{t("login.sent_title", "Lien envoyé")}</p>
                  <p className="text-sm text-green-800 mt-1">
                    {t("login.sent_body_prefix", "Si un compte existe pour")} <strong>{email}</strong>, {t("login.sent_body_suffix", "un lien de connexion vous a été envoyé. Vérifiez votre boîte de réception (le lien est valable")} <strong>15 {t("login.minutes", "minutes")}</strong>).
                  </p>
                </div>
              </div>

              {previewLink && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="preview-link-banner">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2">
                    {t("login.preview_mode", "Mode aperçu (sans email réel)")}
                  </p>
                  <p className="text-xs text-amber-900 mb-3">
                    {t("login.preview_body", "L'envoi Brevo n'est pas configuré dans cet environnement. Cliquez pour simuler la réception du mail :")}
                  </p>
                  <a
                    href={previewLink}
                    data-testid="preview-link-cta"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-apollo-orange-dark hover:underline break-all"
                  >
                    <ExternalLink className="w-4 h-4" /> {t("login.preview_cta", "Ouvrir le lien de connexion")}
                  </a>
                </div>
              )}

              <button
                onClick={reset}
                data-testid="login-back-btn"
                className="text-sm text-text-secondary hover:text-apollo-orange-dark underline"
              >
                ← {t("login.back_email", "Utiliser un autre email")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: oracle visual */}
      <div className="hidden md:flex flex-1 bg-gradient-to-br from-apollo-orange-light via-white to-apollo-orange-light/40 items-center justify-center p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, #FD8100 0%, transparent 50%), radial-gradient(circle at 70% 70%, #C46500 0%, transparent 60%)",
          }}
        />
        <div className="relative z-10 max-w-md text-center">
          <div className="w-32 h-32 mx-auto rounded-full bg-apollo-orange/10 backdrop-blur flex items-center justify-center mb-8 border border-apollo-orange/20">
            <div className="w-24 h-24 rounded-full bg-apollo-orange flex items-center justify-center shadow-2xl shadow-apollo-orange/30">
              <Sparkles className="w-12 h-12 text-white" strokeWidth={1.5} />
            </div>
          </div>
          <h2 className="font-heading font-black text-3xl tracking-tighter text-text-primary mb-3">
            {i18n.language?.startsWith("en") ? (<>Inspired by Delphi.<br />Designed for APOLLO.</>) : (<>Inspiré de Delphes.<br />Conçu pour APOLLO.</>)}
          </h2>
          <p className="font-oracle italic text-lg text-text-primary/80 leading-relaxed">
            {i18n.language?.startsWith("en")
              ? "Where the Pythia revealed the future, DELPHI reads your group and prescribes what must come."
              : "Là où la Pythie révélait l'avenir, DELPHI lit votre groupe et prescrit ce qui doit advenir."}
          </p>
        </div>
      </div>
    </div>
  );
}
